import { Amplify } from "aws-amplify";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
  Navigate,
} from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import { AutoLogout } from "shared/src/components/auto-logout";
import { BreakingChangesReloadGuard } from "shared/src/components/breaking-changes-reload-guard/breaking-changes-reload-guard";
import { DevFeedbackPlugin } from "shared/src/components/dev-feedback";
import Settings from "@/pages/settings";
import ClaimableAuthenticator from "@/components/shared/claimable-authenticator";
import IdentifyUser from "@/components/identify-user";
import PageViewTracker from "@/components/page-view-tracker";
import mantineTheme from "@/mantine-theme";
import { FormProvider } from "@/contexts/form-context";
import { NavigationBlocker } from "@/components/navigation-blocker";
import { DevEnvironmentBanner } from "@/components/dev-environment-banner";
import { signOutUser } from "@/sign-out";

import "./app.scss";
import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import HomePage from "@/pages/home-page/home-page";
import { UserProviderSetupWrapper } from "./components/user-provider-setup-wrapper/user-provider-setup-wrapper";
import ReferPatientInfo from "@/pages/refer-patient-info/refer-patient-info";
import { InitialReferralPage } from "@/pages/initial-referral-page/initial-referral-page";
import { Patient } from "@/pages/patient/patient";
import { Insurance } from "@/pages/insurance/insurance";
import { AppealDestination } from "@/pages/appeal-destination/appeal-destination";
import { Documents } from "@/pages/documents/documents";
import { PrescriberInformation } from "@/pages/prescriber-information/prescriber-information";
import { MedicalInfoTabRoute } from "@/pages/medical-info/medical-info-tab-route";
import { PatientSurvey } from "@/pages/patient-survey/patient-survey";
import { SubmissionDetails } from "@/pages/submission-details/submission-details";
import { LetterOfMedicalNecessityPage } from "@/pages/letter-of-medical-necessity/letter-of-medical-necessity-page";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

function AutoLogoutWrapper() {
  const { authStatus } = useAuthenticator();
  const posthog = usePostHog();

  const handleInactivityLogout = () => {
    posthog.capture("auth_inactivity_logout");
  };

  return (
    <AutoLogout
      onLogout={signOutUser}
      onInactivityLogout={handleInactivityLogout}
      isAuthenticated={authStatus === "authenticated"}
    />
  );
}

function App() {
  const router = sentryCreateBrowserRouter([
    {
      element: (
        <ClaimableAuthenticator>
          <DevEnvironmentBanner />
          <IdentifyUser />
          <AutoLogoutWrapper />
          <UserProviderSetupWrapper>
            <ScrollRestoration />
            <PageViewTracker />
            <BreakingChangesReloadGuard />
            <NavigationBlocker />
            <Outlet />
          </UserProviderSetupWrapper>
        </ClaimableAuthenticator>
      ),
      children: [
        {
          path: "/",
          element: <HomePage />,
        },
        { path: "/refer", element: <InitialReferralPage /> },
        {
          path: "/refer/:appealId/patient-info",
          element: <ReferPatientInfo />,
        },
        {
          path: "/refer/:appealId/enhance",
          element: <Navigate to="/" replace />,
        },
        {
          path: "/appeals/:appealId/strengthen/patient",
          element: <Patient />,
        },
        {
          path: "/appeals/:appealId/strengthen/insurance",
          element: <Insurance />,
        },
        {
          path: "/appeals/:appealId/strengthen/appeal-destination",
          element: <AppealDestination />,
        },
        {
          path: "/appeals/:appealId/strengthen/documents",
          element: <Documents />,
        },
        {
          path: "/appeals/:appealId/strengthen/prescriber-information",
          element: <PrescriberInformation />,
        },
        {
          path: "/appeals/:appealId/strengthen/medical-info/:tabId",
          element: <MedicalInfoTabRoute />,
        },
        {
          path: "/appeals/:appealId/strengthen/patient-survey",
          element: <PatientSurvey />,
        },
        {
          path: "/appeals/:appealId/strengthen/submission-details",
          element: <SubmissionDetails />,
        },
        {
          path: "/appeals/:appealId/strengthen/letter-of-medical-necessity",
          element: <LetterOfMedicalNecessityPage />,
        },
        { path: "/settings", element: <Settings /> },
      ],
    },
  ]);

  return (
    <MantineProvider theme={mantineTheme}>
      <Authenticator.Provider>
        <FormProvider>
          <RouterProvider router={router} />
        </FormProvider>
      </Authenticator.Provider>
      {import.meta.env.VITE_SENTRY_ENV !== "prod" && <DevFeedbackPlugin />}
    </MantineProvider>
  );
}

export default App;
